import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Sparkles, Flame, TrendingUp, TrendingDown, Minus } from "lucide-react";

/**
 * MomentumRibbon — slim weekly-progress strip shown above the Dashboard bento.
 *
 * Pulls `GET /api/me/momentum` once on mount and renders:
 *   • a label ("3 / 5 lessons this week")
 *   • a thin animated bar that fills toward the weekly goal
 *   • a "🔥 today" pill when the student has completed at least one lesson
 *     in the last 24h, so daily momentum is visible at a glance
 *
 * Designed to never push other dashboard content around: single row,
 * unobtrusive accent colour, ~52px tall.
 */
export default function MomentumRibbon() {
    const [data, setData] = useState(null);

    useEffect(() => {
        let live = true;
        api.get("/me/momentum")
            .then((r) => {
                if (live) setData(r.data);
            })
            .catch(() => {
                /* silent — ribbon is purely decorative */
            });
        return () => {
            live = false;
        };
    }, []);

    if (!data) return null;

    const goal = Math.max(1, data.weekly_goal || 5);
    const count = Math.max(0, data.lessons_this_week || 0);
    const pct = Math.min(100, Math.round((count / goal) * 100));
    const hitGoal = count >= goal;
    const today = data.lessons_today || 0;
    const lastWeek = data.lessons_last_week || 0;
    const deltaCount = data.delta_count ?? 0;
    const deltaPct = data.delta_percent;

    // Resolve the trend pill — we only render it when there's something
    // genuinely to say (avoid noisy "0% vs last week" on quiet weeks).
    let trend = null;
    if (count === 0 && lastWeek === 0) {
        trend = null;
    } else if (lastWeek === 0 && count > 0) {
        trend = {
            tone: "up",
            label: `+${count} vs last week`,
            Icon: TrendingUp,
        };
    } else if (deltaCount > 0) {
        trend = {
            tone: "up",
            label: `↑ ${deltaPct}% vs last week`,
            Icon: TrendingUp,
        };
    } else if (deltaCount < 0) {
        trend = {
            tone: "down",
            label: `↓ ${Math.abs(deltaPct)}% vs last week`,
            Icon: TrendingDown,
        };
    } else {
        trend = {
            tone: "flat",
            label: "Same pace as last week",
            Icon: Minus,
        };
    }

    return (
        <div
            className="rounded-xl border border-accent/25 bg-gradient-to-r from-accent/10 via-white to-emerald-50 px-4 sm:px-5 py-3 flex items-center gap-4 flex-wrap"
            data-testid="momentum-ribbon"
        >
            <div className="flex items-center gap-2 text-primary">
                <Sparkles className="h-4 w-4 text-accent" />
                <span className="text-sm font-semibold tabular-nums">
                    {count}
                    <span className="text-muted-foreground font-normal"> / {goal}</span>
                    <span className="text-muted-foreground font-normal ml-1.5">
                        lessons this week
                    </span>
                </span>
            </div>

            <div
                className="flex-1 min-w-[140px] h-1.5 rounded-full bg-primary/10 overflow-hidden"
                role="progressbar"
                aria-valuenow={pct}
                aria-valuemin={0}
                aria-valuemax={100}
            >
                <div
                    className={`h-full transition-[width] duration-700 ease-out rounded-full ${
                        hitGoal
                            ? "bg-emerald-500"
                            : "bg-gradient-to-r from-accent to-emerald-500"
                    }`}
                    style={{ width: `${pct}%` }}
                    data-testid="momentum-bar-fill"
                />
            </div>

            {today > 0 ? (
                <span
                    className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2.5 py-1 text-[11px] font-semibold text-orange-700"
                    data-testid="momentum-today-pill"
                >
                    <Flame className="h-3 w-3" />
                    {today} today
                </span>
            ) : (
                <span
                    className="text-[11px] text-muted-foreground"
                    data-testid="momentum-today-empty"
                >
                    No lessons yet today
                </span>
            )}

            {hitGoal ? (
                <span
                    className="text-[11px] font-semibold text-emerald-700"
                    data-testid="momentum-goal-met"
                >
                    Goal reached!
                </span>
            ) : null}

            {trend ? (
                <span
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                        trend.tone === "up"
                            ? "bg-emerald-100 text-emerald-700"
                            : trend.tone === "down"
                            ? "bg-rose-100 text-rose-700"
                            : "bg-slate-100 text-slate-600"
                    }`}
                    data-testid={`momentum-trend-${trend.tone}`}
                    title={`Last week: ${lastWeek} lesson${lastWeek === 1 ? "" : "s"}`}
                >
                    <trend.Icon className="h-3 w-3" />
                    {trend.label}
                </span>
            ) : null}
        </div>
    );
}
